import React, {
  Fragment,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { Animated, View } from 'react-native';
import { styled, SxProp, useSx } from 'dripsy';
import { useNavigation, useRoute } from '@react-navigation/native';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useModal } from 'react-native-modalfy';

import {
  CollapsibleTabListScene,
  PostList,
  IPostItemProps,
  Separator,
  EmptyResultTypeEnum,
  FlatListRefType,
} from '@components';
import { parseApiError, IS_WEB } from '@utils';
import {
  screens,
  HomeTabNavigatorRoutePropType,
  HomeTabNavigatorNavigationPropType,
} from '@navigation';
import { usePostsModel } from '@models/Posts/PostsModel';
import { useAsyncInit, useLink } from '@hooks';
import {
  AnalyticsService,
  ShareService,
  ToastService,
} from '@services';
import { Post } from '@models/Posts/PostModel';
import { PostTypeEnum } from '@constants';
import { PostEditorButton } from './PostEditorButton';
import { ModalStackParamsType, modals } from '@modals';
import {
  ScrollToTopButton,
  SCROLL_TO_TOP_BUTTON_SIZE,
} from './ScrollToTopButton';
import { AnimatedValueContext } from '@context';
import { User } from '@models/Viewer/UserModel';

const Container = styled(View)({
  flex: 1,
});

const contentContainerStyle: SxProp = {
  flexGrow: 1,
  px: ['$6', '$0'],
  pb: '$6',
};

const containerStyle: SxProp = {
  position: 'absolute',
  right: '$5',
  bottom: 0,
};

type RoutePropType = HomeTabNavigatorRoutePropType<'News'>;
type NavigationPropType = HomeTabNavigatorNavigationPropType<'News'>;

export const MainNewsList = observer(() => {
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const modal = useModal<ModalStackParamsType>();
  const posts = usePostsModel();
  const { t } = useTranslation();
  const { linkTo } = useLink();
  const postListRef = useRef<FlatListRefType>(null);
  const scrollY = useContext(AnimatedValueContext);
  const sx = useSx();

  const translateY = scrollY
    ? scrollY.interpolate({
        // We are using the second value to make the translation of the button by Y axis more abrupt
        // (approximate values)
        inputRange: [0, 2700, 3000],
        outputRange: [
          SCROLL_TO_TOP_BUTTON_SIZE,
          SCROLL_TO_TOP_BUTTON_SIZE,
          -32,
        ],
        extrapolateRight: 'clamp',
      })
    : null;

  useAsyncInit(() => fetch());

  async function fetch() {
    try {
      await posts.list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await posts.list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function navigateToGallery(item: Post, imageIndex: number) {
    if (IS_WEB) {
      modal.openModal(modals.PostViewModal, {
        item,
        imageIndex,
        onNavigateToUser,
      });
    } else {
      nav.navigate(screens.Gallery, {
        images: item.itemData.images,
        imageIndex,
      });
    }
  }

  const onNavigateToUser = useCallback((user: User) => {
    nav.push(screens.UserProfileTabNavigator, {
      id: user.id,
    });
  }, []);

  function onScrollToTop() {
    postListRef.current?.scrollToOffset({
      offset: 0,
      animated: true,
    });
  }

  function navigateToSubscription() {
    nav.navigate(screens.ProfileNavigator, {
      screen: screens.Subscription,
    });
  }

  const newsPostAction = useCallback<IPostItemProps['onPress']>(
    (item: Post, imageIndex: number) => {
      switch (item.type) {
        case PostTypeEnum.UserStatus:
          navigateToGallery(item, imageIndex);
          break;
        case PostTypeEnum.VideoTutorial:
        case PostTypeEnum.AudioTrack:
          if (item.isAvailableForCurrentPlan) {
            if (item.itemData.captionLinkUrl) {
              linkTo(item.itemData.captionLinkUrl);
            }

            if (item.itemData.captionLinkText) {
              AnalyticsService.trackHomeContent({
                contentTitle: item.itemData.captionLinkText,
              });
            }
          } else {
            modal.openModal(modals.SubscribeSuggestionModal, {
              onPress: () => {
                navigateToSubscription();
                modal.closeModal();
              },
            });
          }
          break;
        case PostTypeEnum.InviteFriend:
          if (item.itemData.actionUrl) {
            ShareService.shareUrl(
              { url: item.itemData.actionUrl },
              { clipboardMessage: t('home.linkCopied') },
            );
          }
          break;
        default:
          navigateToGallery(item, imageIndex);
          break;
      }
    },
    [],
  );

  const isLoading =
    posts.list.fetch.inProgress && !posts.list.fetch.hasEverBeenRun;

  let emptyResultType: EmptyResultTypeEnum =
    EmptyResultTypeEnum.Error;

  if (posts.list.fetch.isError) {
    emptyResultType = EmptyResultTypeEnum.Error;
  } else if (posts.list.isEmpty) {
    emptyResultType = EmptyResultTypeEnum.NoPosts;
  }

  return (
    <Container>
      <CollapsibleTabListScene
        listRef={postListRef}
        name={route.name}
      >
        <PostList
          showsVerticalScrollIndicator={false}
          contentContainerSx={contentContainerStyle}
          onPress={newsPostAction}
          isLoading={isLoading}
          isLoadingMore={posts.list.fetchMore.inProgress}
          isRefreshing={posts.list.fetch.inProgressAgain}
          onRefresh={fetch}
          onEndReached={fetchMore}
          data={posts.list.asArray}
          emptyResultType={emptyResultType}
          ListHeaderComponent={() => (
            <Fragment>
              <Separator size="$8" />
              <PostEditorButton />
              <Separator size="$8" />
            </Fragment>
          )}
        />
      </CollapsibleTabListScene>
      {IS_WEB && (
        <Animated.View
          style={[
            sx(containerStyle),
            translateY ? { transform: [{ translateY }] } : null,
          ]}
        >
          <ScrollToTopButton onPress={onScrollToTop} />
        </Animated.View>
      )}
    </Container>
  );
});
